import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet } from "../utils/api";

const PodcastFeedView = () => {
  const { feedId } = useParams();
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [feedName, setFeedName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const episodesPerPage = 9; // Show 9 episodes per page (3x3 grid)

  useEffect(() => {
    if (feedId) {
      fetchFeedDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId]);

  const fetchFeedDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get feed details
      const feedResponse = await apiGet(`/getPodcastFeed`, { feedId });
      
      if (feedResponse.feed) {
        setFeedName(feedResponse.feed.feedName || "Podcast");
      }
      
      // Fetch episodes
      const response = await apiGet(`/getEpisodes`, { feedId });
      
      const allEpisodes = response.episodes || [];
      setEpisodes(allEpisodes);
      
      // Calculate total pages
      setTotalPages(Math.ceil(allEpisodes.length / episodesPerPage));
    } catch (error) {
      console.error("Error fetching feed details:", error);
      setError("Failed to load episodes. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Get current page episodes
  const getCurrentEpisodes = () => {
    const indexOfLastEpisode = currentPage * episodesPerPage;
    const indexOfFirstEpisode = indexOfLastEpisode - episodesPerPage;
    return episodes.slice(indexOfFirstEpisode, indexOfLastEpisode);
  };
  
  // Pagination controls
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when changing page
    window.scrollTo(0, 0);
  };
  
  // Pagination component
  const Pagination = () => {
    // Don't show pagination if there's only one page
    if (totalPages <= 1) return null;
    
    const pageNumbers = [];
    
    // Generate page numbers to display
    if (totalPages <= 5) {
      // Less than 5 pages, show all
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // More than 5 pages, limit display
      if (currentPage <= 3) {
        // Near the start
        pageNumbers.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pageNumbers.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Somewhere in the middle
        pageNumbers.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return (
      <div className="pagination-container" id="feedPagination">
        <button 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
          className="pagination-btn pagination-prev-btn"
          id="paginationPrevBtn"
        >
          &laquo;
        </button>
        
        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`pagination-btn pagination-number-btn ${currentPage === number ? 'pagination-btn-active' : ''}`}
              id={`paginationBtn-${number}`}
              aria-label={`Page ${number}`}
              aria-current={currentPage === number ? 'page' : undefined}
            >
              {number}
            </button>
          )
        ))}
        
        <button 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
          className="pagination-btn pagination-next-btn"
          id="paginationNextBtn"
        >
          &raquo;
        </button>
      </div>
    );
  };

  if (loading) {
    return <div className="feed-loading" id="feedLoading">Loading episodes...</div>;
  }

  if (error) {
    return (
      <div className="feed-error-container" id="feedErrorContainer">
        <div className="feed-error-message" id="feedErrorMessage">{error}</div>
        <button 
          onClick={fetchFeedDetails}
          className="feed-retry-btn"
          id="feedRetryBtn"
        >
          Try Again
        </button>
      </div>
    );
  }

  const currentEpisodes = getCurrentEpisodes();

  return (
    <div className="podcast-feed-content" id="podcastFeedContent">
      <div className="feed-header" id="feedHeader">
        <h2 className="feed-title" id="feedTitle">{feedName}</h2>
        <button 
          onClick={() => navigate(`/podcast/${feedId}/settings`)}
          className="feed-settings-btn"
          id="feedSettingsBtn"
        >
          Feed Settings
        </button>
      </div>
      
      {episodes.length === 0 ? (
        <p className="no-episodes-message" id="noEpisodesMessage">No episodes found for this podcast.</p>
      ) : (
        <>
          <div className="episodes-grid" id="episodesGrid">
            {currentEpisodes.map((episode) => (
              <div 
                key={episode.episodeId} 
                className="episode-card"
                id={`episode-${episode.episodeId}`}
                onClick={() => navigate(`/podcast/${feedId}/episode/${episode.episodeId}`)}
              >
                <div className="episode-thumbnail-container" id={`episodeThumbnail-${episode.episodeId}`}>
                  {episode.imageUrl ? (
                    <img 
                      src={episode.imageUrl} 
                      alt={episode.title} 
                      className="episode-thumbnail"
                      id={`episodeImg-${episode.episodeId}`}
                    />
                  ) : (
                    <div className="episode-thumbnail-placeholder" id={`episodePlaceholder-${episode.episodeId}`}>
                      <span className="placeholder-text">No Image</span>
                    </div>
                  )}
                </div>
                
                <div className="episode-details" id={`episodeDetails-${episode.episodeId}`}>
                  <h3 className="episode-title" id={`episodeTitle-${episode.episodeId}`} title={episode.title}>
                    {episode.title}
                  </h3>
                  
                  <p className="episode-date" id={`episodeDate-${episode.episodeId}`}>
                    {new Date(episode.publishedAt).toLocaleDateString()}
                  </p>
                  
                  {episode.hasTranscription && (
                    <span className="episode-transcribed-badge" id={`episodeTranscribed-${episode.episodeId}`}>
                      Transcribed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <Pagination />
        </>
      )}
    </div>
  );
};

export default PodcastFeedView;